import React, { useEffect, useRef, useState, useCallback } from "react";

interface Level {
  price: number;
  qty: number;
  total: number; // cumulative
}

interface OrderBookProps {
  symbol: string; // e.g. 'BTC'
  precision?: number; // decimal places for price
}

const DEPTH = 14; // rows per side
const WS_BASE = "wss://stream.binance.com:9443/ws";

/**
 * OrderBook
 *
 * Connects to Binance's depth WebSocket for the given symbol.
 * Shows DEPTH ask rows (red) + spread + DEPTH bid rows (green).
 * Each row has a filled bar behind it showing relative size.
 *
 * No API key needed — Binance public stream.
 */
const OrderBook: React.FC<OrderBookProps> = ({ symbol, precision }) => {
  const [asks, setAsks] = useState<Level[]>([]);
  const [bids, setBids] = useState<Level[]>([]);
  const [spread, setSpread] = useState<number | null>(null);
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const isMounted = useRef(true);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);

  // Auto decimal places based on price magnitude
  const dp = useCallback(
    (price: number): number => {
      if (precision !== undefined) return precision;
      if (price >= 10000) return 1;
      if (price >= 1000) return 2;
      if (price >= 100) return 3;
      if (price >= 1) return 4;
      return 6;
    },
    [precision],
  );

  const formatPrice = useCallback(
    (price: number) => {
      const d = dp(price);
      return price.toFixed(d);
    },
    [dp],
  );

  const formatQty = (qty: number): string => {
    if (qty >= 1000) return `${(qty / 1000).toFixed(1)}K`;
    if (qty >= 1) return qty.toFixed(2);
    return qty.toFixed(4);
  };

  const connect = useCallback(() => {
    if (!isMounted.current) return;
    const stream = `${symbol.toLowerCase()}usdt@depth20@100ms`;
    const ws = new WebSocket(`${WS_BASE}/${stream}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMounted.current) return;
      attemptRef.current = 0;
      setConnected(true);
    };

    ws.onmessage = (e: MessageEvent) => {
      if (!isMounted.current) return;
      try {
        const data = JSON.parse(e.data as string);
        // data.asks / data.bids: array of [price, qty] strings
        const rawAsks: [string, string][] = data.asks || [];
        const rawBids: [string, string][] = data.bids || [];

        // Sort: asks ascending (lowest ask at bottom), bids descending
        const sortedAsks = rawAsks
          .map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) }))
          .filter((l) => l.qty > 0)
          .sort((a, b) => a.price - b.price)
          .slice(0, DEPTH);

        const sortedBids = rawBids
          .map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) }))
          .filter((l) => l.qty > 0)
          .sort((a, b) => b.price - a.price)
          .slice(0, DEPTH);

        // Cumulative totals for bar width
        const maxAskQty = sortedAsks.reduce((s, l) => s + l.qty, 0) || 1;
        const maxBidQty = sortedBids.reduce((s, l) => s + l.qty, 0) || 1;
        let cumAsk = 0,
          cumBid = 0;

        const askLevels: Level[] = sortedAsks.map((l) => {
          cumAsk += l.qty;
          return { ...l, total: cumAsk };
        });
        const bidLevels: Level[] = sortedBids.map((l) => {
          cumBid += l.qty;
          return { ...l, total: cumBid };
        });

        setAsks(askLevels.reverse()); // show highest ask at top
        setBids(bidLevels);

        if (sortedAsks.length > 0 && sortedBids.length > 0) {
          const lowestAsk = sortedAsks[0].price;
          const highestBid = sortedBids[0].price;
          setSpread(lowestAsk - highestBid);
          setLastPrice(highestBid);
        }
      } catch {
        /* ignore */
      }
    };

    ws.onerror = () => {
      /* onclose handles reconnect */
    };

    ws.onclose = (e: CloseEvent) => {
      if (!isMounted.current) return;
      setConnected(false);
      if (e.code === 1000) return; // intentional

      const delays = [1000, 2000, 5000, 10000, 30000];
      const delay = delays[Math.min(attemptRef.current, delays.length - 1)];
      attemptRef.current++;
      reconnectRef.current = setTimeout(connect, delay);
    };
  }, [symbol]);

  useEffect(() => {
    isMounted.current = true;
    connect();
    return () => {
      isMounted.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000);
      }
    };
  }, [connect]);

  // Max cumulative for bar sizing
  const maxAskCum = asks.length > 0 ? asks[0].total : 1;
  const maxBidCum = bids.length > 0 ? bids[bids.length - 1].total : 1;

  const spreadPct =
    spread !== null && lastPrice && lastPrice > 0
      ? ((spread / lastPrice) * 100).toFixed(3)
      : null;

  return (
    <div className="flex flex-col h-full bg-card select-none text-[11px] font-mono">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800/60 shrink-0">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider font-display">
          Order Book
        </span>
        <span
          className={`flex items-center gap-1 text-[9px] font-bold ${connected ? "text-primary" : "text-gray-600"}`}
        >
          <span
            className={`w-1 h-1 rounded-full ${connected ? "bg-primary animate-pulse" : "bg-gray-700"}`}
          />
          {connected ? "LIVE" : "…"}
        </span>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-3 px-3 py-1 border-b border-gray-800/30">
        <span className="text-[9px] text-gray-600 uppercase tracking-wide">
          Price (USD)
        </span>
        <span className="text-[9px] text-gray-600 uppercase tracking-wide text-right">
          Qty
        </span>
        <span className="text-[9px] text-gray-600 uppercase tracking-wide text-right">
          Total
        </span>
      </div>

      {/* Asks (sell side — red) — shown top to bottom, highest first */}
      <div
        className="flex flex-col overflow-y-auto"
        style={{ flex: 1, minHeight: 0 }}
      >
        <div className="flex flex-col justify-end">
          {asks.length === 0 ? (
            <div className="flex items-end justify-center py-4">
              <span className="text-gray-700 text-[10px]">Loading…</span>
            </div>
          ) : (
            asks.map((level, i) => {
              const barW = (level.total / maxAskCum) * 100;
              return (
                <div
                  key={`ask-${i}`}
                  className="relative grid grid-cols-3 px-3 py-[3px] hover:bg-red-500/5 transition-colors"
                >
                  {/* Background bar */}
                  <div
                    className="absolute top-0 right-0 bottom-0 bg-red-500/8 pointer-events-none"
                    style={{ width: `${barW}%` }}
                  />
                  <span className="text-red-500 font-semibold relative z-10">
                    {formatPrice(level.price)}
                  </span>
                  <span className="text-gray-400 text-right relative z-10">
                    {formatQty(level.qty)}
                  </span>
                  <span className="text-gray-600 text-right relative z-10">
                    {formatQty(level.total)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Spread row */}
        <div className="flex items-center justify-between px-3 py-1.5 border-y border-gray-800/50 bg-card shrink-0">
          <span className="text-gray-50 font-bold text-xs">
            {lastPrice !== null ? `$${formatPrice(lastPrice)}` : "—"}
          </span>
          <span className="text-gray-500 text-[10px]">
            {spread !== null
              ? `Spread ${formatPrice(spread)} (${spreadPct}%)`
              : "Spread —"}
          </span>
        </div>

        {/* Bids (buy side — green) */}
        <div
          className="flex flex-col overflow-y-auto"
          style={{ flex: 1, minHeight: 0 }}
        >
          {bids.length === 0 ? (
            <div className="flex items-start justify-center py-4">
              <span className="text-gray-700 text-[10px]">Loading…</span>
            </div>
          ) : (
            bids.map((level, i) => {
              const barW = (level.total / maxBidCum) * 100;
              return (
                <div
                  key={`bid-${i}`}
                  className="relative grid grid-cols-3 px-3 py-[3px] hover:bg-primary/5 transition-colors"
                >
                  <div
                    className="absolute top-0 right-0 bottom-0 bg-primary/8 pointer-events-none"
                    style={{ width: `${barW}%` }}
                  />
                  <span className="text-primary font-semibold relative z-10">
                    {formatPrice(level.price)}
                  </span>
                  <span className="text-gray-400 text-right relative z-10">
                    {formatQty(level.qty)}
                  </span>
                  <span className="text-gray-600 text-right relative z-10">
                    {formatQty(level.total)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderBook;
